import { datadogRum } from '@datadog/browser-rum'
import { cloneDeep as _cloneDeep } from 'lodash'
import React, { Component } from 'react'
import {
  Asn,
  DatadogEncodingRun,
  DecodedItem,
  IdentifierType,
  Parcels,
  ProductInfoField,
  ShippingParcel,
  ShippingParcelChecklist,
  Shippings
} from 'stylewhere/api'
import {
  AntennaButton,
  Box,
  Button,
  FullLoadingLayer,
  OperationReadingCounters,
  OperationReadingList,
  Page,
  RemoveMode,
  Spacer,
  Stoplight,
  TagCounter,
  Text
} from 'stylewhere/components'
import { BarcodeModeOptions } from 'stylewhere/components/AntennaButton'
import { config } from 'stylewhere/config'
import { ShippingExtensions } from 'stylewhere/extensions'
import { __, __UP, T } from 'stylewhere/i18n'
import { Routes } from 'stylewhere/pages'
import {
  AppStore,
  checkRequiredField,
  FormSchemaData,
  getDataFromSchema,
  OperationReadingProps,
  OperationReadingProvider,
  OperationReadingState,
  RemoteOperation,
  RfidReader,
  Router,
  Sounds,
  loadOtherParcelsReadings,
  RNEventSource
} from 'stylewhere/shared'
import { CustomError } from 'stylewhere/shared/errors'
import { ShippingOperationConfig } from 'stylewhere/shared/RemoteOperation'
import {
  askUserConfirmation,
  checkItemsCounterError,
  filterErrorItems,
  filterIgnoredAndErrorItems,
  filterIgnoredItems,
  getChecklistItemsCount,
  getOtherParcelsReadingsByProduct,
  getSseEndpoint,
  hasAsyncConfirm,
  hasChecklist,
  isModalError,
  showToast,
  showToastError,
  validateChecklistReadings
} from 'stylewhere/shared/utils'
import { v4 as generateUid } from 'uuid'

export interface ShippingState extends OperationReadingState {
  parcel?: ShippingParcel
  asn?: Asn
  confirming: boolean
  lastAsyncID: string
  sseStatus: boolean
  sseError: boolean
  backRoute?: Routes
  backRouteParams?: Record<string, string>
  removeModeEnabled: boolean
  autostartAntenna?: boolean
  autoConfirmParcel?: boolean
  initReaderTags: string[]
  barcodeItemReading: boolean
  otherParcelsReadings: DecodedItem<string>[]
  redirecting?: boolean
  filterParams?: Record<string, any>
}

export default class ShippingReading<
  P extends OperationReadingProps<ShippingState>,
  S extends ShippingState
> extends Component<P, S> {
  antennaRef
  get matchParams() {
    return Router.getMatchParams(this.props)
  }
  get locationState() {
    return Router.getLocationState<ShippingState>(this.props)
  }
  get operation() {
    return RemoteOperation.getOperationConfig<ShippingOperationConfig>(this.matchParams.opCode)
  }
  get formSchema() {
    return ShippingExtensions.formSchema(this.operation, { parcel: this.locationState.parcel }, ['', 'parcel'])
  }
  isModal = false
  asn?: Asn
  datadogSession: DatadogEncodingRun | undefined = undefined

  public state: S = {
    items: [],
    loading: true,
    formData: {},
    confirming: false,
    lastAsyncID: '',
    sseStatus: false,
    sseError: false,
    removeModeEnabled: false,
    initReaderTags: [],
    barcodeItemReading: false,
    parcel: undefined,
    asn: undefined,
    otherParcelsReadings: [],
    redirecting: false
  } as any

  constructor(props) {
    super(props)
    this.antennaRef = React.createRef()
    this.removeItem = this.removeItem.bind(this)
    // Inizializza i campi che dipendono da this.props dopo super(props)
    this.asn = this.locationState.asn
    // Inizializza lo state con i valori da locationState
    this.state = {
      ...this.state,
      formData: this.locationState.formData ?? {},
      parcel: this.locationState.parcel,
      asn: this.locationState.asn
    } as S
  }

  get isBatchReadingMode() {
    return this.operation.persistParcelContentScannings === 'onBatchRead'
  }
  get operationRemoveModeAllowed() {
    return this.operation.removeMode && ['sku', 'rfid'].includes(this.operation.removeMode)
  }
  get isRemoveModeBySku() {
    return this.operation.removeMode === 'sku'
  }
  get isRemoveModeByRfidRead() {
    return this.operation.removeMode === 'rfid'
  }
  sseProcessedEvents: string[] = []

  kickBackCondition = () => {
    return !this.locationState.formData
  }

  disableFormData = () => {
    return false
  }

  canAutoStartAntenna = () => {
    return true
  }

  async componentDidMount() {
    if (this.kickBackCondition()) {
      this.goBack()
      return
    }

    this.isModal = isModalError(this.operation)

    const userSetAutostopAntennaTimeout: number =
      (await AppStore.getUserSetting('autostopAntennaTimeout', this.operation.id))?.value ||
      this.operation.autostopAntennaTimeout
    const userSetAutostartAntenna: boolean =
      (await AppStore.getUserSetting('autostartAntenna', this.operation.id))?.value || this.operation.autostartAntenna
    const userSetAutoConfirmParcel: boolean =
      (await AppStore.getUserSetting('autoConfirmParcel', this.operation.id))?.value || false
    const userBarcodeItemReading: boolean =
      (await AppStore.getUserSetting('barcodeItemRead', this.operation.id))?.value || false

    // Carica asn se non è disponibile e checklistMode === 'shipment'
    let asn: Asn | undefined = this.locationState.asn
    if (!asn && this.operation.options.checklistMode === 'shipment') {
      const queryParams = Router.getQueryParams()
      const asnId = queryParams.asnId
      if (asnId) {
        try {
          const retrieveAsn = await Shippings.getAsnExtended(asnId)
          const formData = {
            shipmentCode: retrieveAsn.code,
            originPlaceId:
              retrieveAsn.originPlaceId && retrieveAsn.originPlaceId !== null
                ? retrieveAsn.originPlaceId
                : AppStore.defaultWorkstation?.placeId,
            destinationPlaceId:
              retrieveAsn.destinationPlaceId && retrieveAsn.destinationPlaceId !== null
                ? retrieveAsn.destinationPlaceId
                : AppStore.defaultWorkstation?.placeId,
            attributes: retrieveAsn.attributes ?? {}
          }
          asn = await Shippings.startAsn(this.operation, formData)
          this.asn = asn // Aggiorna anche this.asn
        } catch (error) {
          console.warn('Error loading asn:', error)
        }
      }
    }

    // Aggiorna this.asn se è disponibile
    if (asn) {
      this.asn = asn
    }

    let parcel: ShippingParcel | undefined = this.locationState.parcel
    if (this.isBatchReadingMode) {
      try {
        if (!checkRequiredField(this.state.formData, this.formSchema)) {
          await this.openRequiredFieldsModal()
        }
        const createParcelResponse = await this.createParcel()
        parcel = createParcelResponse?.parcel
        if (!parcel) throw new Error('Missing parcel')
      } catch (error) {
        showToastError(error, __(T.error.error), this.isModal)
      }
    }

    const entriesItem: DecodedItem[] = []
    let tagsToAdd: string[] = []
    if (parcel && parcel.parcelEntries && parcel.parcelEntries.length > 0) {
      parcel.parcelEntries.map((entry) => {
        if (entry.item && entry.item !== null) {
          const uhfTag = entry.item.identifiers.filter((e) => e.type === 'UHF_TAG').flatMap(({ code }) => code)
          tagsToAdd = tagsToAdd.concat(uhfTag)
          entriesItem.push({
            item: entry.item,
            statuses: [],
            identifierCode: uhfTag.length > 0 ? uhfTag[0] : ''
          })
        }
      })
    }

    const startAntennaNow =
      this.canAutoStartAntenna() && !userBarcodeItemReading && entriesItem.length === 0 && userSetAutostartAntenna

    RfidReader.setBatchInterval(this.operation.batchInterval)
    RfidReader.setBatchIntervalTagCount(this.operation.batchIntervalTagCount)
    RfidReader.setBatchIntervalTime(this.operation.batchIntervalTime)
    RfidReader.setAutomaticStop(userSetAutostopAntennaTimeout > 0)
    RfidReader.setAutomaticStopTime(userSetAutostopAntennaTimeout)
    RfidReader.setAvoidRestartingTimerOnRead(this.operation.avoidRestartingTimerOnRead)

    // Se checklistMode === 'shipment', imposta il checklist del parcel da asn.checklist
    if (this.operation.options.checklistMode === 'shipment' && asn?.checklist && parcel && !parcel.checklist) {
      parcel.checklist = asn.checklist
      parcel.products = asn.products ?? {}
    }

    OperationReadingProvider.init(
      {
        ...this.operation,
        autostartAntenna: startAntennaNow
      },
      this.locationState,
      this.goBack,
      this.setRfidReaderDecode,
      this.disableFormData()
    )
    this.setState(
      {
        parcel,
        asn,
        items: entriesItem,
        initReaderTags: tagsToAdd,
        sseStatus: !hasAsyncConfirm(this.operation),
        autostartAntenna: userSetAutostartAntenna,
        autoConfirmParcel: userSetAutoConfirmParcel,
        barcodeItemReading: userBarcodeItemReading
      },
      () => {
        this.attachSse()
        if (startAntennaNow) this.initializeDataDogSession()
        // Carica le letture degli altri parcels se checklistMode === 'shipment'
        // TODO questa parte non credo serva, va in contrasto
        // if (this.operation.options.checklistMode === 'shipment' && this.state.asn?.checklist && this.state.parcel) {
        //   this.loadOtherParcelsReadings()
        // }
      }
    )
  }

  componentWillUnmount = () => {
    RNEventSource.destroy(true)
  }

  initializeDataDogSession = async (formSchemaData?: FormSchemaData) => {
    const isActive = await AppStore.activeDatadog()
    if (isActive) {
      this.datadogSession = {
        runId: generateUid().toString(),
        operationCode: this.operation.code,
        startTime: new Date(),
        errors: []
      }
      datadogRum.addAction('startShipping', this.datadogSession)
    }
  }

  loadOtherParcelsReadings = async () => {
    const { parcel, asn } = this.state

    if (!parcel || !asn || this.operation.options.checklistMode !== 'shipment' || !asn.checklist) {
      return
    }

    try {
      const otherParcelsReadings = await loadOtherParcelsReadings(this.operation, asn, parcel.id)
      this.setState({ otherParcelsReadings })
    } catch (error) {
      console.warn('Error loading other parcels readings:', error)
      // Non bloccare l'operazione se fallisce il caricamento delle letture degli altri parcels
      this.setState({ otherParcelsReadings: [] })
    }
  }

  /**
   * Adatta il checklist del parcel sottraendo le quantità già lette negli altri parcels
   * quando checklistMode === 'shipment'
   */
  adaptParcelChecklist = (parcel?: ShippingParcel): ShippingParcelChecklist | undefined => {
    const { otherParcelsReadings } = this.state

    if (
      !parcel?.checklist ||
      this.operation.options.checklistMode !== 'shipment' ||
      !otherParcelsReadings ||
      otherParcelsReadings.length === 0
    ) {
      return parcel?.checklist
    }

    const otherParcelsReadingsByProduct = getOtherParcelsReadingsByProduct(
      otherParcelsReadings,
      this.operation,
      ShippingExtensions,
      'code'
    )

    // Clona il checklist e modifica le quantità
    const adaptedChecklist = _cloneDeep(parcel.checklist)

    // Modifica checklistProducts
    if (adaptedChecklist.checklistProducts) {
      adaptedChecklist.checklistProducts = adaptedChecklist.checklistProducts
        .map((cp) => {
          const alreadyRead = otherParcelsReadingsByProduct[cp.productCode] || 0
          const remaining = cp.quantity - alreadyRead
          return {
            ...cp,
            quantity: Math.max(0, remaining)
          }
        })
        .filter((cp) => cp.quantity > 0) // Rimuovi i prodotti con quantity 0
    }

    // Modifica checklistItems
    // Per CHECKLIST_BY_ITEM, ogni item nel checklistItems aggiunge sempre 1 agli expected
    // (indipendentemente da item.quantity, come visto in OperationReadingProvider.groupItems)
    if (adaptedChecklist.checklistItems) {
      // Raggruppa gli items per productCode
      const itemsByProduct: {
        [productCode: string]: Array<{ productCode: string; itemId?: string; quantity?: number; detected?: number }>
      } = {}
      adaptedChecklist.checklistItems.forEach((item) => {
        if (!itemsByProduct[item.productCode]) {
          itemsByProduct[item.productCode] = []
        }
        itemsByProduct[item.productCode].push(item)
      })

      // Per ogni prodotto, rimuovi gli items già letti
      const filteredItems: typeof adaptedChecklist.checklistItems = []
      Object.keys(itemsByProduct).forEach((productCode) => {
        const alreadyRead = otherParcelsReadingsByProduct[productCode] || 0
        const totalItems = itemsByProduct[productCode].length
        const remaining = totalItems - alreadyRead

        if (remaining > 0) {
          // Mantieni solo i primi 'remaining' items (ogni item aggiunge 1 agli expected)
          const itemsToKeep = itemsByProduct[productCode].slice(0, remaining)
          itemsToKeep.forEach((item) => {
            if (item.itemId) {
              filteredItems.push(item as { itemId: string; productCode: string; quantity?: number; detected?: number })
            }
          })
        }
      })

      adaptedChecklist.checklistItems = filteredItems
    }

    return adaptedChecklist
  }

  canShowConfirmButton = () => {
    const { items } = this.state
    const isItemsAvailable = items.filter(filterIgnoredItems)
    return isItemsAvailable.length > 0
  }

  confirmButtonEnabled = () => {
    if (this.state.autoConfirmParcel) {
      return this.state.initReaderTags.length > 0
    }
    return true
  }

  parcelIsSuitableForConfirmation = () => {
    const { parcel, items } = this.state
    if (!parcel) return false
    const itemsCount = items
      .filter(filterIgnoredAndErrorItems)
      .reduce((acc, item) => acc + OperationReadingProvider.getItemIntrinsicQuantity(item, ShippingExtensions), 0)
    const { expecteds, overQty, unexpecteds } = getChecklistItemsCount(
      items,
      this.operation,
      ShippingExtensions,
      parcel.checklist,
      parcel.products
    )
    return itemsCount === expecteds && !overQty && !unexpecteds
  }

  attachSse = async () => {
    if (!hasAsyncConfirm(this.operation) || !AppStore.loggedUser) {
      return
    }
    const sseUrl = getSseEndpoint(config)
    RNEventSource.destroy(true)
    RNEventSource.addEventListener('open', this.sseEventOpen)
    RNEventSource.addEventListener('message', (e) => this.sseEventMessage(e))
    RNEventSource.addEventListener('error', this.sseEventError)
    RNEventSource.initialize(sseUrl)
  }

  sseEventOpen = () => {
    this.setState({ sseStatus: true })
  }

  sseEventError = async () => {
    this.setState({ sseError: true, sseStatus: false })
  }

  sseEventMessage = async (event) => {
    const { lastAsyncID } = this.state
    const eventData = event.data ? JSON.parse(event.data) : undefined
    if (eventData && eventData.data) {
      if (eventData.data.asyncExecutionId === lastAsyncID) {
        if (eventData.data.executionStatus === 'OK') {
          if (this.sseProcessedEvents.includes(eventData.data.asyncExecutionId)) {
            return
          }
          this.sseProcessedEvents.push(eventData.data.asyncExecutionId)
          try {
            const res = await Parcels.getById(eventData.data.details.operationInstanceId)
            this._confirmSuccess(res, eventData.data.details.lastParcel ?? false)
          } catch (error) {
            this._confirmSuccess()
          }
        } else {
          this._confirmFailed(eventData.data.details || '')
        }
      }
    }
  }

  getDecodeRequest = () => {
    const { parcel } = this.state

    const decodePayload = getDataFromSchema(this.state.formData, this.formSchema)

    decodePayload.operationId = this.operation.id
    decodePayload.parcelCode = decodePayload.parcelCode || parcel?.code
    if (!decodePayload.clientPlaceId) {
      decodePayload.clientPlaceId = AppStore.defaultWorkstation?.placeId
    }
    return {
      url: Shippings.batchValidateEndpoint(this.operation),
      payload: {
        ...decodePayload,
        asn: undefined
        // asn: { ...decodePayload.asn, originPlaceId: AppStore.defaultWorkstation?.placeId }
      }
    }
  }

  setRfidReaderDecode = () => {
    RfidReader.setOnDecodedItemCallback(this.onDecodedItemCallback, this.getDecodeRequest())
  }

  updateParcel = async (action: 'INSERT' | 'UPDATE' | 'CLEAR' | 'REMOVE', itemIds: string[], parcelCode: string) => {
    const parcel = await Shippings.updateParcel(this.operation, {
      operationId: this.operation.id,
      itemIds,
      action,
      parcelCode
    })
    return parcel
  }

  onParcelConfirmIsSuitable = async () => {
    if (this.state.autoConfirmParcel) {
      await this.antennaRef.current.stopReader()
      RfidReader.setAutomaticStop(false)
      RfidReader.stopTimer()
      const userSetAutostopAntennaTimeout: number =
        (await AppStore.getUserSetting('autostopAntennaTimeout', this.operation.id))?.value ||
        this.operation.autostopAntennaTimeout
      setTimeout(
        () => {
          this.setState({ confirming: true }, () => {
            this.onConfirm()
          })
        },
        Math.max(userSetAutostopAntennaTimeout ?? 0, 1500) - 1500
      )
    }
  }

  decodedItemCheck = async (itemMapFromReader: { [tagCode: string]: DecodedItem }) => {
    return true
  }

  onDecodedItemCallback = async (itemMapFromReader: { [tagCode: string]: DecodedItem }) => {
    const { items, formData, removeModeEnabled, parcel } = this.state
    if (removeModeEnabled && this.isRemoveModeByRfidRead) {
      const itemsToRemove = Object.values(itemMapFromReader)
      itemsToRemove.forEach(this.removeItem)
    } else {
      if (!(await this.decodedItemCheck(itemMapFromReader))) return
      const processedItems: DecodedItem[] = await OperationReadingProvider.processDecodedItems(
        this.operation,
        itemMapFromReader,
        items,
        formData,
        ShippingExtensions
      )
      if (this.isBatchReadingMode) {
        if (!this.state.parcel) return
        try {
          await this.updateParcel(
            'INSERT',
            processedItems
              .filter((itm) =>
                Object.values(itemMapFromReader)
                  .map((readerItem) => readerItem.item?.id)
                  .includes(itm.item?.id)
              )
              .filter(filterIgnoredAndErrorItems)
              .flatMap(({ item }) => (item?.id ? item.id : [])),
            this.state.parcel?.code
          )
        } catch (error) {
          showToastError(error, __(T.error.error), this.isModal)
        }
      }
      this.setState({ items: processedItems }, async () => {
        this.onItemsDecodedCallback()
        if (this.datadogSession) {
          this.logDatadogReads(processedItems)
        }

        if (parcel) {
          if (this.parcelIsSuitableForConfirmation()) {
            this.onParcelConfirmIsSuitable()
          }
        }
      })
    }
  }

  private async logDatadogReads(processedItems: DecodedItem[]) {
    const { datadogSession } = this
    if (!datadogSession) return

    const now = Date.now()

    const pushIfNotExists = (list, key, value) => {
      if (!list.some((e) => e[key] === value)) {
        list.push({ offsetMs: now - datadogSession.startTime.getTime(), [key]: value })
      }
    }

    for (const item of processedItems) {
      if (!item.identifierCode || !item.item || !item.item.identifiers) continue
      for (const idtf of item.item.identifiers) {
        if (idtf.code === item.identifierCode) {
          if (idtf.type === 'NFC_TAG') {
            datadogSession.nfcRead = datadogSession.nfcRead || []
            pushIfNotExists(datadogSession.nfcRead, 'nfcRead', idtf.code)
            break
          } else if (idtf.type === 'UHF_TAG') {
            datadogSession.uhfRead = datadogSession.uhfRead || []
            pushIfNotExists(datadogSession.uhfRead, 'uhfRead', idtf.code)
            break
          }
        }
      }
    }
  }

  onItemsDecodedCallback = () => {
    // extensible
  }

  async removeItem(decodedItem: DecodedItem) {
    const { parcel, items } = this.state
    if (this.isBatchReadingMode) {
      if (!parcel) return
      let itemIds: string[] = []
      if (decodedItem.item && decodedItem.item.id) {
        itemIds.push(decodedItem.item.id)
      } else {
        itemIds = Object.values(decodedItem)
          .map((itm) => itm.item?.id)
          .filter(Boolean) as string[]
      }
      if (itemIds.length > 0) {
        await this.updateParcel('REMOVE', itemIds, parcel.code)
      }
    }
    if (this.datadogSession) {
      if (!this.datadogSession.tagRemoved) this.datadogSession.tagRemoved = []
      this.datadogSession.tagRemoved.push({
        offsetMs: Date.now() - this.datadogSession.startTime.getTime(),
        code: decodedItem.identifierCode
      })
      datadogRum.addAction('tagRemoved', this.datadogSession)
    }
    const newItems = OperationReadingProvider.removeItem(decodedItem, items)
    this.setState({ items: newItems, initReaderTags: [] }, this.checkRemoved)
  }

  checkRemoved = () => {
    const { items } = this.state
    const tagCounterDetected = items
      .filter(filterIgnoredItems)
      .reduce((acc, item) => acc + OperationReadingProvider.getItemIntrinsicQuantity(item, ShippingExtensions), 0)
    if (tagCounterDetected === 0) {
      this.setState({ removeModeEnabled: false })
    }
  }

  initRemoveModeByRfid = () => {
    if (this.isRemoveModeByRfidRead) {
      RfidReader.clear()
    }
  }

  onConfirm = async () => {
    const { items } = this.state
    if (items.filter(filterErrorItems).length > 0) {
      showToastError(__(T.error.confirm_not_allowed_with_errors), __(T.error.error), true)
    } else {
      this.setState({ confirming: true }, this._confirm)
    }
  }

  getConfirmData = () => {
    const parcel = this.state.parcel ?? this.locationState.parcel
    const { formData } = this.state
    const confirmData = getDataFromSchema(formData, this.formSchema)
    if (this.asn && this.asn.code && (!confirmData.asn || !confirmData.asn.code)) {
      if (!confirmData.asn) confirmData.asn = {}
      confirmData.asn.code = this.asn.code
    } else if (parcel && parcel.asn && parcel.asn.code && (!confirmData.asn || !confirmData.asn.code)) {
      if (!confirmData.asn) confirmData.asn = {}
      confirmData.asn.code = parcel.asn.code
    }
    if (confirmData.asn) {
      if (this.operation.type === 'INBOUND' && !confirmData.asn?.destinationPlaceId) {
        confirmData.asn = {
          ...confirmData.asn,
          destinationPlaceId: AppStore.defaultWorkstation?.placeId
        } as any
      } else if (this.operation.type === 'OUTBOUND' && !confirmData.asn?.originPlaceId) {
        confirmData.asn = {
          ...confirmData.asn,
          originPlaceId: AppStore.defaultWorkstation?.placeId
        } as any
      } else if (this.operation.type === 'PACKING' && !confirmData.operationPlaceId) {
        confirmData.operationPlaceId = AppStore.defaultWorkstation!.placeId
      }
      if (
        confirmData.asn.destinationPlaceId &&
        typeof confirmData.asn.destinationPlaceId === 'object' &&
        confirmData.asn.destinationPlaceId.id
      ) {
        confirmData.asn.destinationPlaceId = confirmData.asn.destinationPlaceId.id
      }
      if (
        confirmData.asn.originPlaceId &&
        typeof confirmData.asn.originPlaceId === 'object' &&
        confirmData.asn.originPlaceId.id
      ) {
        confirmData.asn.originPlaceId = confirmData.asn.originPlaceId.id
      }
    }
    return confirmData
  }

  createParcel = async () => {
    let parcel = this.state.parcel ?? this.locationState.parcel
    const asn = this.state.asn ?? this.locationState.asn
    const { items } = this.state
    const confirmData = this.getConfirmData()
    if (!parcel) {
      parcel = await this.startParcel(confirmData)
    }
    if (this.operation.options.checklistMode === 'shipment' && asn?.checklist && parcel && !parcel?.checklist) {
      parcel.checklist = asn.checklist
      parcel.products = asn.products ?? {}
    }

    return { parcel, confirmData, items }
  }

  startParcel = async (confirmData) => {
    if (!AppStore.defaultWorkstation?.placeId) throw new Error('Missing workstation place')
    const payload = {
      operationId: this.operation.id,
      attributes: confirmData.attributes,
      ...confirmData,
      asn: { ...confirmData.asn }
    }
    try {
      const parcel = await Shippings.startParcel(this.operation, payload)
      return parcel
    } catch (err) {
      if (err instanceof CustomError) {
        showToastError(
          `${CustomError.interpolateMessage(err.message, payload)}. [${err.errorCode}]`,
          __(T.error.error),
          this.isModal
        )
        return
      }
      console.error(err)
    }
  }

  openRequiredFieldsModal = async (isShowingOnConfirm = false) => {
    try {
      await new Promise((resolve, reject) => {
        AppStore.callbacks.openModalForm(
          resolve,
          this._cancel,
          isShowingOnConfirm ? __UP(T.misc.update_and_confirm) : __UP(T.misc.confirm),
          __UP(T.misc.back)
        )
      })
    } catch (error) {
      this.setState({ confirming: false })
      return
    }
  }

  _cancel = () => {
    this.setState({ confirming: false })
  }

  _confirm = async () => {
    const asyncJobs = hasAsyncConfirm(this.operation)
    try {
      if (this.antennaRef && this.antennaRef.current) {
        await this.antennaRef.current.stopReader()
      }

      //check if some data of schema is required and undefined: if check is false then open Modal Form
      if (this.operation.showModalOnConfirm || !checkRequiredField(this.state.formData, this.formSchema)) {
        await this.openRequiredFieldsModal(true)
      }

      if (hasChecklist(this.operation) && this.state.parcel && this.state.parcel.checklist) {
        // Adatta il checklist se necessario (sottrae prodotti già letti in altre parcel)
        const adaptedChecklist = this.adaptParcelChecklist(this.state.parcel)
        const { validationResult, message } = validateChecklistReadings(
          this.state.items,
          this.operation,
          ShippingExtensions,
          adaptedChecklist,
          this.state.parcel.products
        )
        if (validationResult === 'KO') {
          showToastError(new Error(message), __(T.error.error), this.isModal)
          this.setState({ confirming: false })
          return
        }
        if (validationResult === 'WARNING') {
          const askConfirmResult = await askUserConfirmation(
            __(T.misc.checklist),
            message,
            __(T.misc.no),
            __(T.misc.yes)
          )
          if (!askConfirmResult) {
            this.setState({ confirming: false })
            return
          }
        }
      }

      const parcelResponse = await this.createParcel()
      let parcel: ShippingParcel | undefined = parcelResponse?.parcel
      const confirmData: Record<PropertyKey, any> = parcelResponse?.confirmData ?? {}
      const items = parcelResponse?.items ?? []

      if (!parcel) throw new Error('Missing parcel')

      await ShippingExtensions.beforeConfirm(this.operation, confirmData, items)

      if (!this.isBatchReadingMode) {
        await this.updateParcel('CLEAR', [], parcel.code)
        parcel = await this.updateParcel(
          'INSERT',
          items.filter(filterIgnoredAndErrorItems).flatMap(({ item }) => (item?.id ? item.id : [])),
          parcel.code
        )
      }

      const confirmParcelData: any = {
        operationId: this.operation.id,
        attributes: confirmData.attributes || {},
        ...confirmData,
        parcelCode: parcel.code
      }
      const result: any = await Shippings.confirmParcel(
        this.operation,
        confirmParcelData,
        false,
        asyncJobs,
        confirmData.asn ? true : false
      )
      if (asyncJobs) {
        if (result && result.asyncExecutionId) {
          this.setState({ lastAsyncID: result.asyncExecutionId })
        } else {
          this._confirmFailed()
        }
      } else {
        this._confirmSuccess(result, result.lastParcel ?? false)
      }
    } catch (err: any) {
      this.setState({ confirming: false })
      if (this.datadogSession) {
        this.datadogSession.endTime = new Date()
        this.datadogSession.operationFailedOffsetMs =
          this.datadogSession.endTime.getTime() - this.datadogSession.startTime.getTime()
        if (!this.datadogSession.additionalInfo) this.datadogSession.additionalInfo = {}
        this.datadogSession.additionalInfo.autoConfirmParcel = this.state.autoConfirmParcel
        this.datadogSession.errors.push({ error: err.message })
        datadogRum.addAction('shippingFailed', this.datadogSession)
      }
      if (err instanceof CustomError) {
        const payloadData = getDataFromSchema(this.state.formData, this.formSchema)
        showToastError(
          `${CustomError.interpolateMessage(err.message, payloadData)}. [${err.errorCode}]`,
          __(T.error.error),
          this.isModal
        )
        return
      }
      showToastError(err, __(T.error.error), this.isModal)
    }
  }

  _confirmFailed = (message?: string) => {
    showToastError(message && message != '' ? message : __(T.error.operation_confirm_error), __(T.error.error), true)
    this.setState({ confirming: false })
    if (this.datadogSession) {
      this.datadogSession.endTime = new Date()
      this.datadogSession.operationFailedOffsetMs =
        this.datadogSession.endTime.getTime() - this.datadogSession.startTime.getTime()
      if (!this.datadogSession.additionalInfo) this.datadogSession.additionalInfo = {}
      this.datadogSession.additionalInfo.autoConfirmParcel = this.state.autoConfirmParcel
      this.datadogSession.errors.push({ error: message })
      datadogRum.addAction('shippingFailed', this.datadogSession)
    }
  }

  onConfirmSuccess = async (result?) => {}

  _confirmSuccess = async (result?: any, lastParcel?: boolean) => {
    this.onConfirmSuccess(result)
    const confirmData = this.getConfirmData()
    await ShippingExtensions.afterConfirm(this.operation, confirmData, result || {})

    if (result?.status === 'READY_TO_RECEIVE' || result?.status === 'RECEIVED') {
      showToast({
        title: __(T.misc.success),
        description: __(T.messages.parcel_success),
        status: 'success'
      })
    } else {
      showToast({
        title: __(T.misc.success),
        description: __(T.messages.generic_success, { code: this.operation.description }),
        status: 'success'
      })
    }

    if (this.datadogSession) {
      this.datadogSession.endTime = new Date()
      this.datadogSession.operationSuccessOffsetMs =
        this.datadogSession.endTime.getTime() - this.datadogSession.startTime.getTime()
      if (!this.datadogSession.additionalInfo) this.datadogSession.additionalInfo = {}
      this.datadogSession.additionalInfo.autoConfirmParcel = this.state.autoConfirmParcel
      datadogRum.addAction('shippingSuccess', this.datadogSession)
    }

    if (
      this.operation.shippingMode === 'shipment' &&
      this.asn &&
      lastParcel &&
      this.operation.askToCloseShipmentOnLastParcel !== 'no'
    ) {
      if (this.operation.askToCloseShipmentOnLastParcel === 'askUserConfirmation') {
        if (
          await askUserConfirmation(
            __(T.confirm.post_confirm_action_title),
            __(T.confirm.last_parcel_confirmerd, {
              code: this.asn.code
            }),
            __(T.misc.no),
            __(T.misc.yes)
          )
        ) {
          this.confirmCloseAsn()
        } else {
          this.setState({ confirming: false })
          this.goBack()
        }
      } else {
        this.confirmCloseAsn()
      }
    } else {
      this.setState({ confirming: false })
      this.afterConfirmSuccess()
    }
  }

  confirmCloseAsn = async () => {
    try {
      if (!this.asn) throw new Error('Asn missing')
      await Shippings.confirmAsn(this.operation, this.asn.code, {
        transactionDate: new Date().toISOString()
      })

      showToast({
        title: __(T.misc.success),
        description: __(T.messages.operation_success),
        status: 'success'
      })

      Sounds.success()
    } catch (e) {
      showToastError(e, __(T.error.error), this.isModal)
    } finally {
      this.setState({ confirming: false })
      if (this.operation.templatePath === 'withEncoding') {
        Router.navigate('/shipping/withEncoding/:opCode', { opCode: this.operation.code })
      } else {
        Router.navigate('/shipping/:opCode', { opCode: this.operation.code })
      }
    }
  }

  afterConfirmSuccess = async () => {
    if (['hideSuccessModal', 'disabled'].includes(this.operation.postConfirmAction)) {
      this.goBack()
    } else {
      if (
        await askUserConfirmation(
          __(T.confirm.post_confirm_action_title),
          this.operation.postConfirmAction === 'keepInput'
            ? __(T.confirm.post_confirm_action_keep_input)
            : __(T.confirm.post_confirm_action_change_input),
          __(T.misc.no),
          __(T.misc.yes)
        )
      ) {
        if (this.operation.postConfirmAction === 'keepInput') {
          this.onClearKeepInput()
        } else {
          this.goBack()
        }
      } else {
        this.goDashboard()
      }
    }
  }

  goDashboard = () => {
    Router.navigate('/')
  }

  goBack = () => {
    const filterParams = this.locationState?.filterParams ?? {}
    if (this.locationState?.backRoute) {
      Router.navigate(
        this.locationState?.backRoute,
        {
          ...(this.locationState?.backRouteParams ?? {})
        },
        { state: { filterParams: { ...filterParams } } }
      )
    } else if (this.formSchema.length) {
      Router.navigate('/shipping/:opCode', { opCode: this.operation.code, ...filterParams })
    } else {
      this.goDashboard()
    }
  }

  onClear = async () => {
    const { parcel } = this.state
    if (this.isBatchReadingMode && parcel) {
      await this.updateParcel('CLEAR', [], parcel.code)
    }
    this.setState({ items: [] })
    RfidReader.clear()
  }

  onClearKeepInput = async () => {
    if (this.isBatchReadingMode) {
      const confirmData = this.getConfirmData()
      const parcel = await this.startParcel(confirmData)
      this.setState({ items: [], parcel: parcel })
    } else {
      this.setState({ items: [], parcel: undefined })
    }
    RfidReader.clear()
  }

  showConfirmButton() {
    const { confirming, autoConfirmParcel, initReaderTags } = this.state
    const autoConfirmPlainParcelMode = autoConfirmParcel && initReaderTags.length === 0

    if (this.canShowConfirmButton()) {
      const enabled = this.confirmButtonEnabled()
      return (
        <Button
          style={{ marginTop: 15 }}
          variant={autoConfirmPlainParcelMode ? 'plain' : enabled ? 'primary' : 'ignore'}
          loading={confirming}
          disabled={!enabled}
          title={autoConfirmPlainParcelMode ? __(T.misc.confirm_auto) : __(T.misc.confirm)}
          onClick={this.onConfirm}
        />
      )
    }
    return null
  }

  setFormData = async (fd) => {
    if (!(await ShippingExtensions.formDataIsValid(fd, this.operation, this.formSchema))) return
    this.setState({ formData: fd })
    this.setRfidReaderDecode()
  }

  onRemoveChange: (checked: boolean) => void = (removeModeEnabled) => {
    this.setState({ removeModeEnabled }, this.initRemoveModeByRfid)
  }

  handleOnUserSettingChanged(settingName: string, newValue: string | number | boolean) {
    switch (settingName) {
      case 'autostartAntenna':
        this.setState({ autostartAntenna: newValue as boolean })
        break
      case 'autoConfirmParcel':
        this.setState({ autoConfirmParcel: newValue as boolean })
        break
      case 'autostopAntennaTimeout':
        RfidReader.setAutomaticStop(Number(newValue) > 0)
        RfidReader.setAutomaticStopTime(Number(newValue))
        break
      case 'barcodeItemRead':
        this.setState({ barcodeItemReading: newValue as boolean })
        break
      default:
        break
    }
  }

  upperSideBarComponent: () => React.ReactElement = () => {
    return <></>
  }

  antennaStartButtonEnabled = () => {
    return true
  }

  antennaClearButtonVisible = () => {
    return this.state.items.length !== 0
  }

  overrideProductInfoFields: () => ProductInfoField[] | undefined = () => {
    return undefined
  }

  getHideIdentifierZones: () => boolean = () => {
    return false
  }

  getHideIdentifierCodeByType = (): IdentifierType[] | undefined => {
    return undefined
  }

  getDisableModalRowCounters = () => {
    return false
  }

  getBarcodeModeOptions: () => BarcodeModeOptions | undefined = () => {
    return undefined
  }

  getCustomSidebarBottomComponent = () => {
    return null
  }

  render() {
    if (this.kickBackCondition()) {
      // Naviga immediatamente se i dati necessari non sono disponibili (es. dopo un refresh)
      if (!this.state.redirecting) {
        this.setState({ redirecting: true }, () => {
          this.goBack()
        })
      }
      return null
    }

    const {
      items,
      formData,
      loading,
      parcel,
      sseStatus,
      sseError,
      removeModeEnabled,
      initReaderTags,
      barcodeItemReading,
      otherParcelsReadings
    } = this.state

    const tagCounterDetected = items
      .filter(filterIgnoredItems)
      .reduce((acc, item) => acc + OperationReadingProvider.getItemIntrinsicQuantity(item, ShippingExtensions), 0)
    let tagCounterExpecteds = 0
    let tagCounterUnexpecteds = 0
    if (parcel?.checklist) {
      const { expecteds, unexpecteds, overQty } = getChecklistItemsCount(
        items,
        this.operation,
        ShippingExtensions,
        parcel.checklist,
        parcel.products,
        this.operation.options.checklistMode === 'shipment' ? 'code' : undefined
      )

      // Se checklistMode === 'shipment', sottrai le quantità già lette negli altri parcels
      if (
        this.operation.options.checklistMode === 'shipment' &&
        otherParcelsReadings &&
        otherParcelsReadings.length > 0
      ) {
        const otherParcelsReadingsByProduct = getOtherParcelsReadingsByProduct(
          otherParcelsReadings,
          this.operation,
          ShippingExtensions,
          'code'
        )

        // Calcola gli expected corretti sottraendo le quantità già lette
        // Raggruppa gli items per prodotto e calcola gli expected corretti
        const grouped = OperationReadingProvider.groupItems(
          items,
          ['MIXED_CHECKLIST', 'CHECKLIST_BY_PRODUCT'].includes(parcel.checklist.type),
          this.operation,
          ShippingExtensions,
          parcel.checklist,
          parcel.products,
          false,
          'code'
        )

        let adjustedExpecteds = 0
        for (const group of grouped) {
          const product = group.item ? group.item.product : group.product
          const productCode = product?.code
          if (productCode && otherParcelsReadingsByProduct[productCode]) {
            const alreadyRead = otherParcelsReadingsByProduct[productCode]
            adjustedExpecteds += Math.max(0, group.expected - alreadyRead)
          } else {
            adjustedExpecteds += group.expected
          }
        }

        tagCounterExpecteds = adjustedExpecteds
      } else {
        tagCounterExpecteds = expecteds
      }

      tagCounterUnexpecteds = unexpecteds + overQty
    }

    // Crea un checklist adattato con gli expected corretti per OperationReadingList
    const adaptedChecklist = this.adaptParcelChecklist(parcel)

    return (
      <Page
        title={this.operation.description}
        onBackPress={() => this.goBack()}
        alertOnBackPress={
          this.operation.showAlertOnExit &&
          !this.isBatchReadingMode &&
          items.filter(filterIgnoredAndErrorItems).length > 0
        }
        loading={loading}
        headerRight={
          <>
            {hasAsyncConfirm(this.operation) ? <Stoplight active={sseStatus} /> : undefined}
            {this.operationRemoveModeAllowed && tagCounterDetected > 0 && (
              <RemoveMode onRemoveChange={this.onRemoveChange} />
            )}
          </>
        }
        header={{
          details: {
            data: _cloneDeep(formData),
            formSchema: this.formSchema,
            asn: this.asn,
            setFormData: this.setFormData,
            resetFormData: async (fd) => {
              this.setState({ formData: fd })
            },
            operationId: this.operation.id
          }
        }}
        userSettings={{ operation: this.operation, settings: this.operation.userSettings }}
        onUserSettingChanged={(name, newValue) => this.handleOnUserSettingChanged(name, newValue)}
        enableEmulation
        activeAntennaSettings
        styleSettingsButton={{ marginLeft: this.operationRemoveModeAllowed ? 5 : 15 }}
      >
        {!sseError && (
          <>
            <Page.Sidebar>
              <Box flex style={{ overflowY: 'auto' }}>
                {this.upperSideBarComponent()}
                <TagCounter
                  detected={tagCounterDetected}
                  expected={tagCounterExpecteds}
                  unexpected={tagCounterUnexpecteds}
                />
                <AntennaButton
                  ref={this.antennaRef}
                  mode={barcodeItemReading ? 'barcode' : 'antenna'}
                  decodeRequest={this.getDecodeRequest()}
                  onItemDecoded={this.onDecodedItemCallback}
                  onClear={this.onClear}
                  onStart={async () => {
                    const userSetAutostopAntennaTimeout: number =
                      (await AppStore.getUserSetting('autostopAntennaTimeout', this.operation.id))?.value ||
                      this.operation.autostopAntennaTimeout
                    RfidReader.setAutomaticStop(userSetAutostopAntennaTimeout > 0)
                    RfidReader.setAutomaticStopTime(userSetAutostopAntennaTimeout)
                    if (!this.datadogSession) this.initializeDataDogSession()
                  }}
                  hideClear={!this.antennaClearButtonVisible()}
                  initReaderTags={initReaderTags}
                  disabled={!this.antennaStartButtonEnabled()}
                  barcodeModeOptions={this.getBarcodeModeOptions()}
                />
                {checkItemsCounterError(items) && (
                  <>
                    <Spacer />
                    <OperationReadingCounters
                      operation={this.operation}
                      items={items}
                      hideIdentifiersByType={this.getHideIdentifierCodeByType()}
                      hideIdentifiersZones={this.getHideIdentifierZones()}
                      overrideProductInfoFields={this.overrideProductInfoFields()}
                      disableModalRowCounters={this.getDisableModalRowCounters()}
                    />
                  </>
                )}
              </Box>
              {this.showConfirmButton()}
              {this.getCustomSidebarBottomComponent()}
            </Page.Sidebar>
            <Page.Content notBoxed>
              <OperationReadingList
                removeItemCallback={this.removeItem}
                extension={ShippingExtensions}
                items={items}
                operation={this.operation}
                checklist={parcel && hasChecklist(this.operation) ? adaptedChecklist : undefined}
                products={parcel && hasChecklist(this.operation) ? parcel.products : undefined}
                overrideProductInfoFields={this.overrideProductInfoFields()}
                hideIdentifiersZones={this.getHideIdentifierZones()}
                hideIdentifiersByType={this.getHideIdentifierCodeByType()}
                removeModeEnabled={removeModeEnabled && this.isRemoveModeBySku}
              />
            </Page.Content>
          </>
        )}
        {sseError && (
          <Box flex center>
            <Text bold fontSize={20}>
              {__(T.error_messages.connection_in_progress_title)}
            </Text>
            <Text style={{ marginTop: 10 }}>{__(T.error_messages.connection_in_progress_text)}</Text>
          </Box>
        )}
        {!sseStatus && !sseError && (
          <FullLoadingLayer
            message={!sseStatus ? __(T.messages.connection_in_progress) : __(T.messages.operation_in_progress)}
          />
        )}
      </Page>
    )
  }
}
